import { Formik, Form } from 'formik';
import Modal from '@/components/ui/modals';
import { securityValidation } from '@/utils/validation/user';
import { useTranslations } from 'next-intl';
import Input from '@/components/ui/inputFormik';
import InputMobile from '@/components/ui/inputFormik/input-mobile';
import InputPassword from '@/components/ui/inputFormik/input-password';
import { Button } from '@/components/ui/buttons';
import { Spinner } from '@/components/ui/forms';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import {
  changeUserName,
  changeUserEmail,
  changeUserPassword,
  sendOtp,
  verifyOtp,
} from '@/services/user/account';
import { useState } from 'react';
import { getCookie } from 'cookies-next';
import { useSession } from 'next-auth/react';

const initialValues = {
  firstname: '',
  lastname: '',
  mobilenumber: '',
  email: '',
  currentPassword: '',
  password: '',
  confirmPassword: '',
  otp: '', // Add OTP field
};

const onError = (errorMessage, t) => {
  toast.error(errorMessage || t('common.update_failed_key'));
};

const onSuccess = (t, setIsOpen) => {
  toast.success(t('common.update_success_key'));
  setIsOpen(false);
};

function EditModal({ isOpen, setIsOpen, session }) {
  const t = useTranslations('');
  const mage = getCookie('mage');
  const { update } = useSession();
  const isPassword = isOpen === 'password';
  const isName = isOpen === 'name';
  const isEmail = isOpen === 'email';
  const isPhone = isOpen === 'phone_number';

  const [otpSent, setOtpSent] = useState(false);
  const [submittedPhone, setSubmittedPhone] = useState('');

  // Update user name
  const { trigger: updateName, isMutating: isUpdatingName } = useSWRMutation(
    '/api/user/change-name',
    async (_, { arg: values }) => {
      const response = await changeUserName({
        customerId: session?.user?.id,
        currentPassword: values.currentPassword,
        mage,
        ...values,
      });
      if (!response.data) {
        throw new Error(response?.error?.error?.message || '');
      }
      return response;
    }
  );
  // Update user email
  const { trigger: updateEmail, isMutating: isUpdatingEmail } = useSWRMutation(
    '/api/user/change-email',
    async (_, { arg: values }) => {
      const response = await changeUserEmail({
        customerId: session?.user?.id,
        currentPassword: values.currentPassword,
        mage,
        newEmail: values.email,
      });
      if (!response.data) {
        throw new Error(response?.error?.error?.message || '');
      }
      return response;
    }
  );

  // Update user password
  const { trigger: updatePassword, isMutating: isUpdatingPassword } =
    useSWRMutation('/api/user/change-password', async (_, { arg: values }) => {
      const response = await changeUserPassword({
        customerId: session?.user?.id,
        mage,
        ...values,
      });
      if (!response.data) {
        throw new Error(response?.error?.error?.message || '');
      }
      return response;
    });

  // send OTP
  const { trigger: sendOtpMutation, isMutating: isSendingOtp } = useSWRMutation(
    '/customer/register/otp/send',
    async (_, { arg: mobilenumber }) => {
      const response = await sendOtp({
        mobilenumber,
        mage,
      });
      if (!response.data) {
        throw new Error(response?.error?.error || 'Failed to send OTP');
      }
      return response;
    }
  );
  
  // verify OTP Mobile Number
  const { trigger: verifyMobileOtpMutation, isMutating: isVerifyingMobileOtp } =
    useSWRMutation('/api/user/verify-otp', async (_, { arg: otp }) => {
      console.log({ otp, submittedPhone });
      const response = await verifyOtp({
        otp,
        mobilenumber: submittedPhone.slice(1),
        mage,
      });
      if (!response.data) {
        throw new Error(response?.error?.error?.message || 'Invalid OTP');
      }
      return response;
    });

  const handleSubmit = async (values) => {
    try {
      // update name
      if (isName) {
        await updateName({
          firstname: values.firstname,
          lastname: values.lastname,
          currentPassword: values.currentPassword,
        });
        await update({
          ...session?.user,
          firstname: values.firstname,
          lastname: values.lastname,
        });
        onSuccess(t, setIsOpen);
      }
      // update email
      else if (isEmail) {
        await updateEmail({
          email: values.email,
          currentPassword: values.currentPassword,
        });
        await update({
          ...session?.user,
          email: values.email,
        });
        onSuccess(t, setIsOpen);
      }
      // update password
      else if (isPassword) {
        await updatePassword({
          currentPassword: values.currentPassword,
          newPassword: values.password,
          confirmPassword: values.confirmPassword,
        });
        onSuccess(t, setIsOpen);
      }
      // update phone
      else if (isPhone) {
        if (!otpSent) {
          await sendOtpMutation(values.mobilenumber.slice(1));
          setSubmittedPhone(values.mobilenumber);
          setOtpSent(true);
        } else {
          await verifyMobileOtpMutation(values.otp);
          onSuccess(t, setIsOpen);
          setOtpSent(false);
          setSubmittedPhone('');
          update({
            ...session?.user,
            mobilenumber: values.mobilenumber,
          });
        }
      }
    } catch (error) {
      onError(error?.message, t);
    }
  };

  const isSubmitting =
    isUpdatingName ||
    isUpdatingEmail ||
    isUpdatingPassword ||
    isSendingOtp ||
    isVerifyingMobileOtp;

  return (
    <Modal
      isOpen={true}
      onClose={() => {
        setIsOpen(false);
        setOtpSent(false);
        setSubmittedPhone('');
      }}
      title={`${t('user.change_your_key')} ${t('user.' + isOpen + '_key')}`}
      maxWidth='max-sm:max-w-11/12 max-md:max-w-xl max-lg:max-w-2xl lg:max-w-[829px]'
      headerTitleClassName='pt-[13px] pb-[15px] max-sm:px-[15px] max-md:px-[25px] max-lg:px-[31px] lg:px-[37px]'
      titleClassName='!font-bold text-xl max-sm:text-base max-lg:text-lg'
      closeClassName='max-sm:size-5 max-md:size-6'
    >
      <div className='px-[37px] pt-[22px] pb-[33px] max-lg:px-[31px] max-md:px-[25px] max-sm:px-[15px] max-sm:pt-[13px] max-sm:pb-5 lg:px-[37px]'>
        <Formik
          initialValues={{
            ...initialValues,
            firstname: session?.user?.firstname || '',
            lastname: session?.user?.lastname || '',
            email: session?.user?.email || '',
            mobilenumber:
              session?.user?.mobilenumber?.replace('00966', '0') || '',
          }}
          validationSchema={securityValidation(
            t,
            isName,
            isEmail,
            isPhone,
            isPassword,
            otpSent
          )}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting: formikSubmitting }) => (
            <Form
              className='grid grid-cols-1 gap-2 lg:gap-4'
              disabled={isSubmitting || formikSubmitting}
            >
              {/* Name */}
              {isName && (
                <>
                  <Input
                    labelClassName='max-sm:text-xs max-sm:font-semibold'
                    name='firstname'
                    id='first-name'
                    title={t('common.First Name')}
                    aria-label={t('common.First Name')}
                  />
                  <Input
                    labelClassName='max-sm:text-xs max-sm:font-semibold'
                    name='lastname'
                    id='last-name'
                    title={t('common.Last Name')}
                    aria-label={t('common.Last Name')}
                  />
                </>
              )}

              {isEmail && (
                <Input
                  name='email'
                  labelClassName='max-sm:text-xs max-sm:font-semibold'
                  id='email'
                  title={t('user.email_key')}
                  aria-label={t('user.email_key')}
                />
              )}

              {(isName || isPassword || isEmail) && (
                <InputPassword
                  name='currentPassword'
                  labelClassName='max-sm:text-xs max-sm:font-semibold'
                  id='currentPassword'
                  title={
                    isName
                      ? t('user.password_key')
                      : t('user.current_password_key')
                  }
                  aria-label={
                    isName
                      ? t('user.password_key')
                      : t('user.current_password_key')
                  }
                />
              )}
              {/* Password */}
              {isPassword && (
                <>
                  <InputPassword
                    name='password'
                    id='password'
                    labelClassName='max-sm:text-xs max-sm:font-semibold'
                    title={t('user.new_password_key')}
                    aria-label={t('user.new_password_key')}
                  />
                  <InputPassword
                    name='confirmPassword'
                    id='confirmPassword'
                    labelClassName='max-sm:text-xs max-sm:font-semibold'
                    title={t('user.confirm_new_password_key')}
                    aria-label={t('user.confirm_new_password_key')}
                  />
                </>
              )}

              {/* Phone */}
              {isPhone && (
                <>
                  {!otpSent ? (
                    <InputMobile
                      id='mobilenumber'
                      name='mobilenumber'
                      labelClassName='max-sm:text-xs max-sm:font-semibold'
                      title={t('user.primary_mobile_number_key')}
                      aria-label={t('user.primary_mobile_number_key')}
                      disabled={otpSent}
                    />
                  ) : (
                    <>
                      <p className='text-sm'>
                        {t('common.an_otp_has_been_sent_key', {
                          phone: submittedPhone,
                        })}
                      </p>
                      <Input
                        id='otp'
                        name='otp'
                        labelClassName='max-sm:text-xs max-sm:font-semibold'
                        title={t('common.otp')}
                        aria-label={t('common.otp')}
                      />
                    </>
                  )}
                </>
              )}

              <div className='mt-1.5 max-sm:mt-1 lg:mb-2'>
                <Button
                  className='w-full cursor-pointer rounded-lg bg-red-600 py-1 font-bold text-white hover:bg-red-600/70 max-md:text-[15px] max-sm:h-9 max-sm:text-xs sm:py-1.5'
                  type='submit'
                  disabled={isSubmitting || formikSubmitting}
                >
                  {isPhone
                    ? otpSent
                      ? t('common.verify_and_update_key')
                      : t('common.send_otp_key')
                    : t('user.change_key')}
                  {(isSubmitting || formikSubmitting) && <Spinner />}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}

export default EditModal;
